import React, { useEffect, useState } from "react";
import { Save, Plus, Trash2, Loader2 } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";

const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange/40";

const Field = ({ label, value, onChange, area, placeholder }) => (
  <label className="block">
    <span className="text-xs font-semibold text-navy">{label}</span>
    {area
      ? <textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${inputCls} mt-1`} />
      : <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`${inputCls} mt-1`} />}
  </label>
);

const Card = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
    <h2 className="font-display font-bold text-navy mb-4">{title}</h2>
    {children}
  </div>
);

const ListEditor = ({ items, onChange, template, render }) => (
  <div className="space-y-3">
    {(items || []).map((it, i) => (
      <div key={i} className="rounded-xl border border-slate-100 bg-slate-50 p-4 relative">
        <button onClick={() => onChange(items.filter((_, x) => x !== i))} className="absolute top-3 right-3 text-red-400 hover:text-red-600" data-testid={`footer-list-remove-${i}`}><Trash2 size={15} /></button>
        {render(it, (patch) => onChange(items.map((x, xi) => (xi === i ? { ...x, ...patch } : x))))}
      </div>
    ))}
    <Button variant="outline" size="sm" onClick={() => onChange([...(items || []), { ...template }])} className="rounded-full" data-testid="footer-list-add"><Plus size={14} className="mr-1" /> Add</Button>
  </div>
);

export default function FooterManager() {
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { adminApi.footer.get().then(setForm).catch(() => {}); }, []);
  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const setNested = (parent, k, v) => setForm((f) => ({ ...f, [parent]: { ...f[parent], [k]: v } }));

  const save = async () => {
    setSaving(true);
    try { await adminApi.footer.save(form); toast({ title: "Footer saved" }); }
    catch { toast({ title: "Couldn't save", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  if (!form) return <p className="text-sm text-slate-400">Loading…</p>;

  return (
    <div data-testid="footer-manager">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-display font-extrabold text-2xl text-navy">Footer</h1>
          <p className="text-sm text-slate-500">Edit the site-wide footer content.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-orange hover:bg-orange-dark text-white rounded-full shrink-0" data-testid="footer-save">
          {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Save size={15} className="mr-1.5" />} Save changes
        </Button>
      </div>

      <Card title="About / tagline">
        <Field label="Tagline" value={form.tagline} onChange={(v) => set("tagline", v)} area />
      </Card>

      <Card title="Book a Call button">
        <Field label="Booking link (opens when visitors click 'Book a Call' anywhere on the site)" value={form.booking_url} onChange={(v) => set("booking_url", v)} placeholder="https://calendly.com/cass-online/mtd-itsa-discovery-call" />
        <p className="text-xs text-slate-400 mt-2">Paste any booking URL (Calendly, Microsoft Bookings, etc.). Leave blank to hide the booking buttons.</p>
      </Card>

      <Card title="Accreditations & awards">
        <p className="text-xs font-semibold text-slate-500 mb-2">Badges (e.g. Xero Platinum Partner, ACSP Registered)</p>
        <ListEditor items={form.badges} onChange={(v) => set("badges", v)} template={{ label: "" }}
          render={(it, upd) => (<div className="pr-6"><Field label="Badge text" value={it.label} onChange={(v) => upd({ label: v })} /></div>)} />
        <p className="text-xs font-semibold text-slate-500 mb-2 mt-4">Awards / finalist recognition</p>
        <ListEditor items={form.awards} onChange={(v) => set("awards", v)} template={{ label: "" }}
          render={(it, upd) => (<div className="pr-6"><Field label="Award text" value={it.label} onChange={(v) => upd({ label: v })} /></div>)} />
      </Card>

      <Card title="Popular service links">
        <ListEditor items={form.quick_links} onChange={(v) => set("quick_links", v)} template={{ label: "", url: "" }}
          render={(it, upd) => (
            <div className="grid md:grid-cols-2 gap-3 pr-6">
              <Field label="Label" value={it.label} onChange={(v) => upd({ label: v })} />
              <Field label="Link (e.g. /services/vat)" value={it.url} onChange={(v) => upd({ url: v })} />
            </div>
          )} />
      </Card>

      <Card title="Contact details">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Phone" value={form.contact?.phone} onChange={(v) => setNested("contact", "phone", v)} />
          <Field label="Email" value={form.contact?.email} onChange={(v) => setNested("contact", "email", v)} />
          <Field label="Hours" value={form.contact?.hours} onChange={(v) => setNested("contact", "hours", v)} />
          <Field label="Address" value={form.contact?.address} onChange={(v) => setNested("contact", "address", v)} />
        </div>
      </Card>

      <Card title="Social media links">
        <ListEditor items={form.socials} onChange={(v) => set("socials", v)} template={{ platform: "LinkedIn", url: "" }}
          render={(it, upd) => (
            <div className="grid md:grid-cols-2 gap-3 pr-6">
              <Field label="Platform (LinkedIn, Facebook, Instagram, YouTube, Twitter)" value={it.platform} onChange={(v) => upd({ platform: v })} />
              <Field label="URL" value={it.url} onChange={(v) => upd({ url: v })} />
            </div>
          )} />
      </Card>

      <Card title="Newsletter sign-up">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Heading" value={form.newsletter?.heading} onChange={(v) => setNested("newsletter", "heading", v)} />
          <Field label="Text" value={form.newsletter?.text} onChange={(v) => setNested("newsletter", "text", v)} area />
        </div>
      </Card>

      <Card title="Companies House information">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Company name" value={form.companies_house?.company_name} onChange={(v) => setNested("companies_house", "company_name", v)} />
          <Field label="Company number" value={form.companies_house?.number} onChange={(v) => setNested("companies_house", "number", v)} />
          <Field label="VAT number" value={form.companies_house?.vat} onChange={(v) => setNested("companies_house", "vat", v)} />
          <Field label="Registered office" value={form.companies_house?.registered_office} onChange={(v) => setNested("companies_house", "registered_office", v)} />
        </div>
      </Card>
    </div>
  );
}
